// LLM generated - Model Claude 4 Sonnet

import type { ChatMessage, MessageReaction, MessageUser, MessagePayload } from '../types';

// =============================================================================
// CHAT MODE
// =============================================================================

/**
 * Chat mode determines the type of chat channel:
 * - 'bidirectional': User-to-user messages (chat tab)
 * - 'unidirectional': Posts/announcements from admins (posts tab)
 */
export type ChatMode = 'bidirectional' | 'unidirectional';

// =============================================================================
// MESSAGE EVENTS
// =============================================================================

export type PusherMessageCreatedPayload = ChatMessage;

export interface PusherMessageUpdatedPayload {
  id: string;
  payload?: MessagePayload;
  isPinned?: boolean;
  updatedAt: string;
}

export interface PusherMessageDeletedPayload {
  id: string;
  deletedAt: string;
}

// =============================================================================
// REACTION EVENTS
// =============================================================================

export interface PusherReactionAddedPayload {
  messageId: string;
  code: string;
  userId: string;
  handle: string;
  avatarUrl?: string;
  newCount: number;
}

export interface PusherReactionRemovedPayload {
  messageId: string;
  code: string;
  userId: string;
  newCount: number;
}

// =============================================================================
// CHAT STATE EVENTS
// =============================================================================

/**
 * Payload for 'chat-enabled', 'chat-disabled', 'chat-deactivated' events.
 */
export interface PusherChatStatePayload {
  chatId: string;
  isEnabled: boolean;
  reason?: string;
}

// =============================================================================
// USER EVENTS
// =============================================================================
export interface PusherUserBannedPayload {
  userId: string;
  chatId: string;
  reason?: string;
}
export interface PusherMemberEventPayload {
  userId: string;
  handle?: string;
  avatarUrl?: string;
}

// =============================================================================
// CALLBACK TYPES
// =============================================================================

/**
 * Unified callback options for Pusher chat event handlers.
 * These callbacks receive the full payload data from Pusher events,
 * allowing direct cache updates without additional API calls.
 */
export interface PusherChatCallbacks {
  onMessageCreated?: (chatMode: ChatMode, message: PusherMessageCreatedPayload) => void;
  onMessageUpdated?: (chatMode: ChatMode, update: PusherMessageUpdatedPayload) => void;
  onMessageDeleted?: (chatMode: ChatMode, payload: PusherMessageDeletedPayload) => void;
  onReactionAdded?: (chatMode: ChatMode, payload: PusherReactionAddedPayload) => void;
  onReactionRemoved?: (chatMode: ChatMode, payload: PusherReactionRemovedPayload) => void;
  onChatStateChanged?: (chatMode: ChatMode, payload: PusherChatStatePayload) => void;
  onUserBanned?: (chatMode: ChatMode, payload: PusherUserBannedPayload) => void;
  onMemberChanged?: (
    chatMode: ChatMode,
    event: 'added' | 'removed',
    payload: PusherMemberEventPayload
  ) => void;
}

// =============================================================================
// CONNECTION CALLBACKS
// =============================================================================

export interface PusherConnectionCallbacks {
  onConnectionLost?: () => void;
  onConnectionRestored?: () => void;
  onSubscriptionError?: (error: Error) => void;
  onEventTimeout?: (eventType: string, chatMode: ChatMode) => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Timeout in milliseconds before falling back to HTTP when expecting a Pusher event.
 * Matches the timeout used in usePusherFetch.
 */
export const PUSHER_TIMEOUT = 10_000;
